import { uniqBy } from "es-toolkit/array";
import type { PointOfInterest } from "../../../@types/mapviewxs";
import { textFromTD } from "../../../utils/text";
import type { AvailableFilter, AvailableFilterOption, FilterKey } from "../types";

/**
 * Waits for the body overflow style to be cleared after the filter modal dialog closes and then executes the provided callback.
 * @param callback - Function to execute after body overflow is cleared
 */
export function onFilterDialogStyleClear(callback: () => void): void {
  const checkBodyOverflow = () => {
    if (document.body.style.overflow !== "hidden") {
      callback();
    } else {
      requestAnimationFrame(checkBodyOverflow);
    }
  };
  requestAnimationFrame(checkBodyOverflow);
}

export function extractAvailableFilters(pointsOfInterest: PointOfInterest[]): AvailableFilter[] {
  const conditionMap = new Map<string, number>();

  pointsOfInterest.forEach((poi) => {
    const itemCondition = textFromTD(poi.itemCard?.itemCondition);
    if (itemCondition) {
      conditionMap.set(itemCondition, (conditionMap.get(itemCondition) ?? 0) + 1);
    }
  });

  const filters: AvailableFilter[] = [];

  if (conditionMap.size > 0) {
    const conditionOptions: AvailableFilterOption[] = Array.from(conditionMap.keys()).map((condition, index) => ({
      fieldId: `${condition.split(" ").join("-")}-${index.toString()}`,
      label: condition,
      isEnabled: false,
    }));

    filters.push({
      label: "Condition",
      filterKey: "condition",
      options: conditionOptions,
    });
  }

  filters.push({
    label: "Price",
    filterKey: "price",
    minValue: undefined,
    maxValue: undefined,
  });

  return filters;
}

export function isPriceFilterActive(filter: AvailableFilter): boolean {
  if (!filter) return false;
  if (filter.minValue === undefined && filter.maxValue === undefined) return false;

  return true;
}

export function extractFilter(availableFilters: AvailableFilter[], key: FilterKey): AvailableFilter | undefined {
  return availableFilters.find((filter) => filter.filterKey === key);
}

function matchesConditionFilter(poi: PointOfInterest, filter: AvailableFilter): boolean {
  if (!filter.options) return true;

  const enabledConditions = filter.options.filter((option) => option.isEnabled).map((option) => option.label);

  if (enabledConditions.length === 0) return true;

  const poiCondition = textFromTD(poi.itemCard?.itemCondition);
  return poiCondition !== undefined && enabledConditions.includes(poiCondition);
}

function matchesPriceFilter(poi: PointOfInterest, filter: AvailableFilter): boolean {
  const itemPrice = poi.itemCard?.card?.displayPrice?.value?.value;

  if (typeof itemPrice !== "number") {
    return false;
  }

  const hasMinFilter = filter.minValue !== undefined;
  const hasMaxFilter = filter.maxValue !== undefined;

  if (!hasMinFilter && !hasMaxFilter) {
    return true;
  }

  if (hasMinFilter && itemPrice < filter.minValue!) {
    return false;
  }

  if (hasMaxFilter && itemPrice > filter.maxValue!) {
    return false;
  }

  return true;
}

function matchesFilter(poi: PointOfInterest, filter: AvailableFilter): boolean {
  if (filter.filterKey === "condition") {
    return matchesConditionFilter(poi, filter);
  }

  if (filter.filterKey === "price") {
    return matchesPriceFilter(poi, filter);
  }

  return true;
}

export function getFilteredItems(pointsOfInterest: PointOfInterest[], availableFilters: AvailableFilter[]): PointOfInterest[] {
  return pointsOfInterest.filter((poi) => {
    return availableFilters.every((filter) => matchesFilter(poi, filter));
  });
}

export function updateFilter(availableFilters: AvailableFilter[], filterKey: FilterKey, updates: Partial<AvailableFilter>): AvailableFilter[] {
  const result = availableFilters.map((filter) => {
    if (filter.filterKey === filterKey) {
      const updated = {
        ...filter,
        ...updates,
      };
      return updated;
    }
    return filter;
  });

  return result;
}

export function hasActiveFilters(availableFilters?: AvailableFilter[]): boolean {
  if (!availableFilters || availableFilters.length === 0) {
    return false;
  }

  const hasActive = availableFilters.some((filter) => {
    if (filter.filterKey === "condition" && filter.options) {
      return filter.options.some((option) => option.isEnabled);
    }
    if (filter.filterKey === "price") {
      return filter.minValue !== undefined || filter.maxValue !== undefined;
    }
    return false;
  });

  return hasActive;
}

export function getActiveFilters(availableFilters: AvailableFilter[]): AvailableFilter[] {
  const result: AvailableFilter[] = [];

  availableFilters.forEach((filter) => {
    if (filter.filterKey === "condition" && filter.options) {
      filter.options.forEach((option) => {
        const existingConditionFilter = result.find(({ filterKey }) => filterKey === "condition");
        if (option.isEnabled && existingConditionFilter) {
          existingConditionFilter.options?.push(option);
        } else if (option.isEnabled && !existingConditionFilter) {
          result.push({
            label: filter.label,
            filterKey: filter.filterKey,
            options: [option],
          });
        }
      });
    }
    if (filter.filterKey === "price" && (filter.minValue !== undefined || filter.maxValue !== undefined)) {
      result.push({
        label: filter.label,
        filterKey: filter.filterKey,
        minValue: filter.minValue ?? undefined,
        maxValue: filter.maxValue ?? undefined,
      });
    }
  });

  return result;
}

export function mergeFilters(existingFilters: AvailableFilter[], newFilters: AvailableFilter[]): AvailableFilter[] {
  const newFilterKeys = new Set(newFilters.map((f) => f.filterKey));
  const existingOnlyFilters = existingFilters.filter((f) => !newFilterKeys.has(f.filterKey));

  const merged = newFilters.map((newFilter) => {
    const existingFilter = existingFilters.find((f) => f.filterKey === newFilter.filterKey);

    if (!existingFilter) {
      return newFilter;
    }

    if (newFilter.filterKey === "price") {
      return {
        ...newFilter,
        minValue: existingFilter.minValue,
        maxValue: existingFilter.maxValue,
      };
    }

    if (!newFilter.options || !existingFilter.options) {
      return newFilter;
    }

    const newAreaOptionLabels = new Set(newFilter.options.map((opt) => opt.label));

    const allOptions = [...(existingFilter.options || []), ...(newFilter.options || [])];
    const uniqueOptions = uniqBy(allOptions, ({ label }) => label);

    const optionsToKeep = uniqueOptions.filter((option) => {
      const existsInNewArea = newAreaOptionLabels.has(option.label);
      return existsInNewArea || option.isEnabled;
    });

    const mergedOptions = optionsToKeep.toSorted((a, b) => Number(a.fieldId.split("-")[1]) - Number(b.fieldId.split("-")[1]));

    return {
      ...newFilter,
      options: mergedOptions,
    };
  });

  return [...merged, ...existingOnlyFilters];
}

export function getAvailableFiltersToDisplay(pointsOfInterest: PointOfInterest[], currentAvailableFilters: AvailableFilter[]): AvailableFilter[] {
  const filtersFromNewArea = extractAvailableFilters(pointsOfInterest);
  const userActiveFilters = getActiveFilters(currentAvailableFilters);

  const hasActiveFilters = userActiveFilters.length > 0;
  if (hasActiveFilters) {
    return mergeFilters(userActiveFilters, filtersFromNewArea);
  }

  return filtersFromNewArea;
}

export function resetFilters(availableFilters: AvailableFilter[]): AvailableFilter[] {
  return availableFilters.map((filter) => {
    if (filter.filterKey === "condition" && filter.options) {
      return {
        ...filter,
        options: filter.options.map((option) => ({
          ...option,
          isEnabled: false,
        })),
      };
    }
    if (filter.filterKey === "price") {
      return {
        ...filter,
        minValue: undefined,
        maxValue: undefined,
      };
    }
    return filter;
  });
}
